import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import TabNavItem from '../common/tabs/TabNavItem';
import TabContent from '../common/tabs/TabContent';
import EmailProcessTable from './EmailProcessTable';
import SmsProcessTable from './SmsProcessTable';
import CallProcessTable from './CallProcessTable';

const EMPTY_SEARCH = { term: '', applied: '' };

function ProcessTabContainer() {
    const [activeTab, setActiveTab] = useState('email-tab');
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);
    const [searches, setSearches] = useState({
        'email-tab': { ...EMPTY_SEARCH },
        'sms-tab': { ...EMPTY_SEARCH },
        'call-tab': { ...EMPTY_SEARCH },
    });

    const activeSearch = searches[activeTab] || EMPTY_SEARCH;
    const showSearch =
        activeTab === 'email-tab' || activeTab === 'sms-tab' || activeTab === 'call-tab';

    useEffect(() => {
        if (!showSearch) return undefined;
        const timer = setTimeout(() => {
            setSearches((prev) => {
                const current = prev[activeTab];
                if (!current || current.term === current.applied) return prev;
                return {
                    ...prev,
                    [activeTab]: { ...current, applied: current.term },
                };
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [activeTab, showSearch, activeSearch.term]);

    const setActiveSearchTerm = (term) => {
        setSearches((prev) => ({
            ...prev,
            [activeTab]: { ...(prev[activeTab] || EMPTY_SEARCH), term },
        }));
    };

    const applyActiveSearch = (term) => {
        setSearches((prev) => ({
            ...prev,
            [activeTab]: { term, applied: term },
        }));
    };

    const clearActiveSearch = () => {
        setSearches((prev) => ({
            ...prev,
            [activeTab]: { ...EMPTY_SEARCH },
        }));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (activeSearch.term === activeSearch.applied) return;
        applyActiveSearch(activeSearch.term);
    };

    const handleClearTitleSearch = () => {
        clearActiveSearch();
    };

    const emailSearch = searches['email-tab'] || EMPTY_SEARCH;
    const smsSearch = searches['sms-tab'] || EMPTY_SEARCH;
    const callSearch = searches['call-tab'] || EMPTY_SEARCH;

    return (
        <Card className="card-padding shadow-sm border-0">
            <Card.Body className="p-3">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3 nav-tabs-bordered flex-wrap">
                    <ul className="nav nav-tabs flex-grow-1 mb-0 border-0">
                        <TabNavItem
                            id="email-tab"
                            title="Email Queue"
                            icon="bi bi-envelope"
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabNavItem
                            id="sms-tab"
                            title="SMS Queue"
                            icon="bi bi-chat-dots"
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabNavItem
                            id="call-tab"
                            title="Call Queue"
                            icon="bi bi-telephone"
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                    </ul>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                        {showSearch && (
                            <form onSubmit={handleSearchSubmit} className="process-list-receiver-search">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text" aria-hidden="true">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by receiver or patient…"
                                        value={activeSearch.term}
                                        onChange={(e) => setActiveSearchTerm(e.target.value)}
                                        aria-label="Search by receiver or patient name"
                                    />
                                    {activeSearch.term && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={handleClearTitleSearch}
                                            aria-label="Clear search"
                                            title="Clear search"
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    )}
                                </div>
                            </form>
                        )}
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm flex-shrink-0"
                            onClick={() => setSearchPanelOpen((open) => !open)}
                            aria-expanded={searchPanelOpen}
                            title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                            disabled={!showSearch}
                        >
                            <i className="bi bi-funnel me-1"></i>
                            Filter
                        </button>
                    </div>
                </div>
                <div className="tab-content">
                    <TabContent id="email-tab" activeTab={activeTab}>
                        <EmailProcessTable
                            searchPanelOpen={searchPanelOpen}
                            searchTerm={emailSearch.term}
                            appliedSearch={emailSearch.applied}
                            onClearSearch={() =>
                                setSearches((prev) => ({
                                    ...prev,
                                    'email-tab': { ...EMPTY_SEARCH },
                                }))
                            }
                        />
                    </TabContent>
                    <TabContent id="sms-tab" activeTab={activeTab}>
                        <SmsProcessTable
                            searchPanelOpen={searchPanelOpen}
                            searchTerm={smsSearch.term}
                            appliedSearch={smsSearch.applied}
                            onClearSearch={() =>
                                setSearches((prev) => ({
                                    ...prev,
                                    'sms-tab': { ...EMPTY_SEARCH },
                                }))
                            }
                        />
                    </TabContent>
                    <TabContent id="call-tab" activeTab={activeTab}>
                        <CallProcessTable
                            searchPanelOpen={searchPanelOpen}
                            searchTerm={callSearch.term}
                            appliedSearch={callSearch.applied}
                            onClearSearch={() =>
                                setSearches((prev) => ({
                                    ...prev,
                                    'call-tab': { ...EMPTY_SEARCH },
                                }))
                            }
                        />
                    </TabContent>
                </div>
            </Card.Body>
        </Card>
    );
}

export default ProcessTabContainer;
